import React from "react";
import { ArrowLeft, CreditCard, Clock, AlertTriangle, Shield } from "lucide-react";

const RefundCancellation = () => {
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEBB5] to-[#FFF8E6]">
      {/* Header */}
      <div className="bg-[#67142D] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-source-serif-4 font-bold">Refund & Cancellation Policy</h1>
              <p className="text-lg mt-2 font-inter">
                Vishwa Sanatan Mahapeeth - Financial Policy
              </p>
            </div>
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <p className="text-[#67142D] font-semibold mb-4">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="text-[#67142D]/80 font-inter leading-relaxed">
              Vishwa Sanatan Mahapeeth is a spiritual organization committed to religious, social, environmental, and humanitarian causes. We appreciate your contributions and support. Please read this policy carefully before making any donation, membership payment, or financial transaction.
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <CreditCard className="w-6 h-6" />
              1. Donations & Financial Contributions
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>All donations made to Vishwa Sanatan Mahapeeth are final, non-refundable, and non-transferable under any circumstances.</li>
                <li>Donations may be made through various modes, including online payment gateways, bank transfers, and other authorized channels.</li>
                <li>Once a donation is processed, it cannot be canceled, reversed, or modified for any reason.</li>
                <li>No donor shall have the right to claim a refund, reimbursement, or chargeback on any donation made.</li>
                <li>Donations are used for charitable activities at the organization's discretion. Donors have no rights over fund allocation or utilization.</li>
              </ul>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6" />
              2. Membership & Subscription Fees
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Membership fees paid to Vishwa Sanatan Mahapeeth are non-refundable and non-transferable.</li>
                <li>Once registered as a member or volunteer, fees cannot be refunded, even in cases of withdrawal or termination of membership.</li>
                <li>The organization reserves the right to approve, reject, or terminate any membership without obligation to refund fees.</li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              3. Event Participation & Cancellations
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth organizes various religious, cultural, and community events.
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Contributions made towards events or sponsorships are non-refundable, regardless of participation.</li>
                <li>In case of event rescheduling, modification, or cancellation due to unforeseen circumstances, no refunds or compensation will be provided.</li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4 flex items-center gap-3">
              <Clock className="w-6 h-6" />
              4. Refund & Cancellation Timeline
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Since most donations and payments are non-refundable, timelines generally do not apply. However, in exceptional cases (such as accidental duplicate payments), requests may be considered.
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Any such request must be submitted via email to <strong>info@sanatanmahapeeth.org</strong> within <strong>3 working days</strong> of the transaction date.</li>
                <li>The organization will review each request on a case-by-case basis and respond within <strong>7 business days</strong>.</li>
                <li>If approved, the refund will be processed within <strong>10 business days</strong> from the date of approval.</li>
                <li>Refunds, if any, will be made through the original mode of payment only.</li>
              </ul>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              5. Payment Gateway & Transaction Issues
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                Vishwa Sanatan Mahapeeth uses third-party payment gateways for online transactions and is not responsible for payment failures, banking errors, or technical issues.
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>In case of a failed transaction where the amount is deducted, users must contact their bank or payment provider for resolution.</li>
                <li>Any disputes regarding online payments must be handled directly with the payment gateway provider.</li>
              </ul>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              6. No Chargebacks or Disputes
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Once a donation or payment is made, chargebacks, disputes, or reversals through banks or payment providers are not permitted.</li>
                <li>In case of unauthorized transactions, donors must immediately notify their bank or financial institution.</li>
                <li>The organization is not responsible for fraudulent transactions made through third-party payment systems.</li>
              </ul>
            </div>
          </div>

          {/* Section 7 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              7. Legal Jurisdiction & Dispute Resolution
            </h2>
            <div className="space-y-4 text-[#67142D]/80 font-inter">
              <p>
                All legal disputes related to transactions and payments shall be subject to the exclusive jurisdiction of the courts in Haridwar, Uttarakhand, India.
              </p>
              <p>
                Any concerns or disputes must be addressed through written communication to Vishwa Sanatan Mahapeeth at info@sanatanmahapeeth.org before any legal action.
              </p>
              <p>
                Vishwa Sanatan Mahapeeth reserves the right to modify or update this Refund & Cancellation Policy at any time without prior notice.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              8. Contact Information
            </h2>
            <div className="bg-[#67142D]/5 p-6 rounded-lg">
              <div className="space-y-3 text-[#67142D] font-inter">
                <p><strong>📍 Vishwa Sanatan Mahapeeth</strong></p>
                <p><strong>🌐 Website:</strong> www.sanatanmahapeeth.org</p>
                <p><strong>📧 Email:</strong> info@sanatanmahapeeth.org</p>
                <p><strong>📞 Phone:</strong> +91-9520050068 / +91-9520050038</p>
                <p><strong>📍 Address:</strong> तीर्थ आश्रम, शिवलोक कॉलोनी, भगत सिंह चौराहे के पास, हरिद्वार, उत्तराखंड - 249401</p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 font-inter">
                  By making a donation, paying a membership fee, or engaging in any financial transaction with Vishwa Sanatan Mahapeeth, you acknowledge that you have read, understood, and agreed to this Refund & Cancellation Policy.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#67142D]/20 pt-8 mt-8">
            <div className="text-center">
              <p className="text-[#67142D]/60 font-inter text-sm">
                © 2025 Vishwa Sanatan Mahapeeth. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundCancellation;
